import React, { memo, useContext, useEffect, useMemo } from 'react';

import GadgetLoadError from 'components/GadgetLoadError';
import { readStudioBlock } from 'components/Studio/canvasMapping';
import { useReportGadgetRequest } from 'contextProviders/GadgetStatusBoundary';
import VisualisationCacheContext from 'contexts/VisualisationCacheContext';
import Typography from 'kingpin/atoms/Typography';

import FlexCentered from '../Common/FlexCentered';
import ErrorBoundary from '../Common/ErrorBoundary';
import Card from '../Common/Card';
import styled from 'styled-components';
import Row from '../Common/Row';
import isV5ChartDef from '../../types/visTypeCheckers/isV5ChartDef';
import V5Gadget from '../V5Gadget';
import DashboardGadgetProvider from '../../contextProviders/DashboardGadgetProvider';
import IFrameGadget from '../IFrameGadget';
import ImageGadget from '../ImageGadget';
import VideoGadget from '../VideoGadget';
import TextGadget from '../TextGadget';
import MetricListGadget from '../MetricListGadget';
import SingleMetricDateMatrixContainer from '../SingleMetricDateMatrix';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isGauge from '../../types/visTypeCheckers/isGauge';
import Gauge from '../Gauge';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import RemindersGadget from '../RemindersGadget';
import DashboardGadgetDateScopeProvider from '../../contextProviders/DashboardGadgetDateScopeProvider';
import cardTypeCheckers from '../../types/cardTypeCheckers';
import Tooltip from '../Tooltip';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import RankingMatrix from '../RankingMatrix';
import ScorecardGadget from '../Scorecard/ScorecardGadget';
import GoalGadget from 'components/Goals/GoalCard/GoalGadget';
import Icon from '../../kingpin/atoms/Icon';
import Button from '../../kingpin/atoms/Button';
import Loading from '../Loading/Loading';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import PaceMatrix from '../PaceMatrix';
import isGroupVsRemainder from '../../types/visTypeCheckers/isGroupVsRemainder';
import GroupVsRemainder from '../GroupVsRemainder';
import isEntityInformationTable from '../../types/visTypeCheckers/isEntityInformationTable';
import EntityInformationTable from '../EntityInformationTable';
import FilterPlatesContext from '../../contexts/FilterPlatesContext';
import MetricCacheContext from '../../contexts/MetricCacheContext';
import ReportGadgetDateScopeProvider from '../../contextProviders/ReportGadgetDateScopeProvider';
import ReportGadgetProvider from '../../contextProviders/ReportGadgetProvider';
import useGetMetricIdsFromVis from '../../hooks/useGetMetricIdsFromVis';
import useRegisterDashboardGadgetScope from '../../hooks/useRegisterDashboardGadgetScope';
import reportGadgetHasCustomFilters from '../../utils/reportGadgetHasCustomFilters';
import StudioCompositeCardView from './StudioCompositeCardView';
import StudioGadgetBody from './StudioGadgetBody';
import GadgetFullscreenProvider from '../GadgetFullscreen/GadgetFullscreenProvider';

const CardDiv = styled(Card)<{ isEditing: boolean }>`
  padding: 0;
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  padding-top: ${(props) => (props.isEditing ? 40 : 0)}px;
  box-shadow: unset;
  border: 1px solid var(--color-border-tertiary);
  overflow: hidden;

  .opaque-unless-mouse-over {
    transition: opacity 0.3s;
    opacity: 0.6;
  }

  &:hover {
    .opaque-unless-mouse-over {
      opacity: 1;
    }
  }
`;

const RemoveWrapper = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  pointer-events: none;
`;

const RemoveInner = styled.div`
  pointer-events: auto;
  cursor: pointer;
`;

const DragWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  cursor: pointer;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 4px;
  padding-bottom: 4px;

  background-color: var(--color-bg-frosted);
  &:hover {
    opacity: 0.7;
  }
`;

const RenderGadget = ({ gadget }: { gadget: VisualisationDefinition }) => {
  return (
    <GadgetFullscreenProvider>
      {isV5ChartDef(gadget) && <V5Gadget chartDefinition={gadget} />}
      {isSingleMetricDateMatrix(gadget) && (
        <SingleMetricDateMatrixContainer gadget={gadget} />
      )}
      {isGauge(gadget) && <Gauge gauge={gadget} />}
      {isRemindersGadget(gadget) && <RemindersGadget gadget={gadget} />}
      {isRankingMatrix(gadget) && <RankingMatrix gadget={gadget} />}
      {isPaceMatrix(gadget) && <PaceMatrix gadget={gadget} />}
      {isGroupVsRemainder(gadget) && <GroupVsRemainder gadget={gadget} />}
      {isEntityInformationTable(gadget) && (
        <EntityInformationTable gadget={gadget} />
      )}
    </GadgetFullscreenProvider>
  );
};

const RenderGadgetMemo = memo(RenderGadget);

// Publishes this dashboard gadget's resolved scope (its own date range + filters)
// to the Insights agent. Renders nothing; self-gates via InsightsViewScopeContext
// (no-op unless a dashboard has provided its entityId). Must sit inside
// DashboardGadgetProvider + DashboardGadgetDateScopeProvider so it resolves this
// gadget's own scope, not the dashboard's (there is none).
const RegisterDashboardGadgetScope = () => {
  useRegisterDashboardGadgetScope();
  return null;
};

const VisDefinitionNotFound = () => (
  <FlexCentered style={{ height: '100%', opacity: 0.5 }}>
    <Icon icon="warning" color={'var(--color-status-warning)'} />
    <div style={{ marginTop: 4 }}>
      <Typography.Body type="Body 12">Visualisation not found</Typography.Body>
    </div>
  </FlexCentered>
);

// A gadget whose definition couldn't be fetched (after a retry): it may well
// exist, so it's shown as failed rather than missing, and reports itself
// failed so a scheduled render retries rather than sending it
const GadgetFetchFailed = () => {
  const reportRequest = useReportGadgetRequest();
  useEffect(() => {
    reportRequest('failed-handled');
  }, [reportRequest]);
  return <GadgetLoadError message="Couldn't load this gadget" />;
};

const MetricNotFound = () => (
  <FlexCentered style={{ height: '100%', opacity: 0.5 }}>
    <Icon icon="warning" color={'var(--color-status-warning)'} />
    <div style={{ marginTop: 4 }}>
      <Typography.Body type="Body 12">Unable to load metrics</Typography.Body>
    </div>
  </FlexCentered>
);

// A gadget's body: as Studio draws it, when Studio saved it (and its Studio
// settings still hold), else as a classic gadget
const GadgetBody = ({
  chartDefinition,
  studioCard,
  layout,
}: {
  chartDefinition: VisualisationDefinition;
  studioCard: Studio.GadgetSidecarCard | undefined;
  layout: CardLayout;
}) =>
  studioCard ? (
    <StudioGadgetBody
      studioCard={studioCard}
      chartDefinition={chartDefinition}
      layout={layout}
    />
  ) : (
    <RenderGadgetMemo gadget={chartDefinition} />
  );

const DashboardGadgetCardContent = ({
  dashboardGadget,
  hasMetricErrors,
  isDragging,
  studioCard,
  layout,
}: {
  dashboardGadget: DashboardGadget;
  hasMetricErrors: boolean;
  isDragging: boolean;
  studioCard: Studio.GadgetSidecarCard | undefined;
  layout: CardLayout;
}) => {
  if (hasMetricErrors) {
    return <MetricNotFound />;
  }
  return (
    <DashboardGadgetDateScopeProvider dashboardGadget={dashboardGadget}>
      {isDragging ? (
        <FlexCentered>
          <Loading />
        </FlexCentered>
      ) : (
        <DashboardGadgetProvider
          dashboardGadget={dashboardGadget}
          key={`${dashboardGadget.id}-${dashboardGadget.updatedOn}-${
            dashboardGadget.comparison
              ? dashboardGadget.comparison.compareType
              : ''
          }`}
        >
          <RegisterDashboardGadgetScope />
          <GadgetBody
            chartDefinition={dashboardGadget.chartDef}
            studioCard={studioCard}
            layout={layout}
          />
        </DashboardGadgetProvider>
      )}
    </DashboardGadgetDateScopeProvider>
  );
};

const ReportCanvasCard = ({
  isEditing,
  onRemoveClicked,
  card,
  chartDefinition,
  dashboardGadget,
  reportGadget,
  isExporting,
  isDragging,
}: {
  isEditing: boolean;
  onRemoveClicked: () => void;
  card: CanvasCard.Card;
  chartDefinition?: VisualisationDefinition;
  dashboardGadget?: DashboardGadget;
  reportGadget?: ReportGadgets.ReportGadget;
  isExporting: boolean;
  isDragging: boolean;
}) => {
  const filterContext = useContext(FilterPlatesContext);
  const parentDrillDowns = filterContext?.drillDowns || [];
  const setParentDrillDowns = filterContext?.setDrillDowns;
  const parentScope = filterContext?.scope || [];
  const isParentDefaultFiltersDisabled =
    filterContext?.isDefaultFiltersDisabled || false;
  const { isIdErrored } = useContext(MetricCacheContext);
  const { isGadgetIdFailed } = useContext(VisualisationCacheContext);
  const getMetricIds = useGetMetricIdsFromVis();
  const hasReportGadgetMetricErrors =
    !!reportGadget && getMetricIds(reportGadget.chartDef).some(isIdErrored);
  const hasDashboardGadgetMetricErrors =
    !!dashboardGadget &&
    getMetricIds(dashboardGadget.chartDef).some(isIdErrored);
  // The Studio settings the gadget was saved with, while they hold
  const studioCard = useMemo(
    () =>
      dashboardGadget
        ? readStudioBlock({ kind: 'dashboard', gadget: dashboardGadget })
        : reportGadget
          ? readStudioBlock({ kind: 'report', gadget: reportGadget })
          : undefined,
    [dashboardGadget, reportGadget],
  );
  return (
    <CardDiv data-testid={card.layout.i} isEditing={isEditing}>
      {isEditing && (
        <div>
          <DragWrapper className={'draggableHandle'}>
            <Tooltip
              content={`${card.layout.w} x ${card.layout.h}`}
              position="right"
            >
              <Icon icon="drag" />
            </Tooltip>
          </DragWrapper>
          <RemoveWrapper>
            <RemoveInner>
              <Row>
                <Button
                  onClick={() => {
                    onRemoveClicked();
                  }}
                  icon={'cross'}
                  type={'Secondary'}
                  size={'Small'}
                />
              </Row>
            </RemoveInner>
          </RemoveWrapper>
        </div>
      )}
      <ErrorBoundary>
        <>
          {isDragging && !cardTypeCheckers.isDashboardGadget(card) && (
            <FlexCentered>
              <Loading />
            </FlexCentered>
          )}
          {!isDragging && (
            <React.Fragment>
              {cardTypeCheckers.isText(card) && (
                <TextGadget textContentId={card.content.textContentId} />
              )}
              {cardTypeCheckers.isVideo(card) && (
                <VideoGadget storagePath={card.content.videoSrc} />
              )}
              {cardTypeCheckers.isImage(card) && (
                <ImageGadget card={card} isEditing={isEditing} />
              )}
              {cardTypeCheckers.isIFrame(card) && (
                <IFrameGadget snippet={card.content.iFrameSnippet} />
              )}
              {cardTypeCheckers.isMetricList(card) && (
                <GadgetFullscreenProvider>
                  <MetricListGadget
                    metricListGadgetId={card.content.metricListId}
                  />
                </GadgetFullscreenProvider>
              )}
              {cardTypeCheckers.isChartDefinition(card) &&
                (chartDefinition ? (
                  <RenderGadgetMemo gadget={chartDefinition} />
                ) : (
                  <VisDefinitionNotFound />
                ))}
              {cardTypeCheckers.isScorecard(card) && (
                <ScorecardGadget
                  scorecardId={card.content.scorecardId}
                  numberOfPeriods={card.content.numberOfPeriods}
                />
              )}
              {cardTypeCheckers.isGoal(card) && (
                <GoalGadget goalId={card.content.goalId} />
              )}
              {cardTypeCheckers.isStudioComposite(card) && (
                <StudioCompositeCardView card={card} />
              )}
              {cardTypeCheckers.isDashboardGadget(card) &&
                !dashboardGadget &&
                (isGadgetIdFailed(card.content.dashboardGadgetId) ? (
                  <GadgetFetchFailed />
                ) : (
                  <VisDefinitionNotFound />
                ))}
              {cardTypeCheckers.isReportGadget(card) &&
                !reportGadget &&
                isGadgetIdFailed(card.content.reportGadgetId) && (
                  <GadgetFetchFailed />
                )}
              {cardTypeCheckers.isReportGadget(card) && !!reportGadget && (
                <>
                  {hasReportGadgetMetricErrors ? (
                    <MetricNotFound />
                  ) : reportGadgetHasCustomFilters(reportGadget) ? (
                    <ReportGadgetDateScopeProvider reportGadget={reportGadget}>
                      <ReportGadgetProvider
                        reportGadget={reportGadget}
                        parentDrillDowns={parentDrillDowns}
                        setParentDrillDowns={setParentDrillDowns}
                        parentScope={parentScope}
                        isParentDefaultFiltersDisabled={
                          isParentDefaultFiltersDisabled
                        }
                      >
                        <GadgetBody
                          chartDefinition={reportGadget.chartDef}
                          studioCard={studioCard}
                          layout={card.layout}
                        />
                      </ReportGadgetProvider>
                    </ReportGadgetDateScopeProvider>
                  ) : (
                    <ReportGadgetProvider
                      reportGadget={reportGadget}
                      parentDrillDowns={parentDrillDowns}
                      setParentDrillDowns={setParentDrillDowns}
                      parentScope={parentScope}
                      isParentDefaultFiltersDisabled={
                        isParentDefaultFiltersDisabled
                      }
                    >
                      <GadgetBody
                        chartDefinition={reportGadget.chartDef}
                        studioCard={studioCard}
                        layout={card.layout}
                      />
                    </ReportGadgetProvider>
                  )}
                </>
              )}
            </React.Fragment>
          )}
          {cardTypeCheckers.isDashboardGadget(card) && !!dashboardGadget && (
            <DashboardGadgetCardContent
              dashboardGadget={dashboardGadget}
              hasMetricErrors={hasDashboardGadgetMetricErrors}
              isDragging={isDragging}
              studioCard={studioCard}
              layout={card.layout}
            />
          )}
        </>
      </ErrorBoundary>
    </CardDiv>
  );
};

export default ReportCanvasCard;
